import { useQuery } from "@apollo/client";
import { navigate, RouteComponentProps } from "@reach/router";
import React, { useState, useEffect } from "react";
import { getViewer } from "types/gql-types";
import { useSnackbar } from "components/haven-design/snackbar";
import { Roles, Viewer } from "components/auth/types";
import { userLoginToken } from "utils/general-utils";
import { LoadingSpinner } from "components/graphql-views";
import { VIEWER_QUERY } from "./query";

export { Roles };

const viewerHasToken = () => !!userLoginToken();

const userHasRole = (viewerRoles: Roles[], allowedRoles: Roles[]) => {
  if (viewerRoles.includes(Roles.Admin)) return true; // admin always has permission
  // if a set of user roles has one or more of allowed roles, return true
  return viewerRoles.some((role) => allowedRoles.includes(role));
};

/**
 * HOC to wrap a page and only render if viewer has the correct role(s)
 * passes parent props through and adds viewer prop
 **/
export const authorizeForRole = <P extends RouteComponentProps<{viewer: Viewer}>>(
  allowedRoles: Roles[],
  Component: React.ComponentType<Omit<P, "viewer">>,
):React.ComponentType<P> => function Authorizer (props: P) {
  const [showSpinner, setShowSpinner] = useState(false);
  const query = useQuery<getViewer>(VIEWER_QUERY);
  const { openSnackbar } = useSnackbar();

  if (!viewerHasToken()) {
    openSnackbar({ message: "Please log in to access this page", status: "alert", duration: 5000 });
    localStorage.setItem("requestedPath", location.pathname);

    setTimeout(() => navigate("/login"), 0);
    return null;
  }

  // don't show the loading indicator right away to prevent spinner flash
  useEffect(() => {
    const timer = setTimeout(() => setShowSpinner(true), 800);
    return () => clearTimeout(timer);
  }, []);

  if (query.loading) return showSpinner ? <LoadingSpinner/> : null;

  if (!query.data || query.data.viewer === null) return null;

  const {
    viewer,
    viewer: { roles },
  } = query.data;

  if (userHasRole(roles as Roles[], allowedRoles)) {
    return <Component {...props} viewer={viewer} />;
  }

  return <div>Not authorized.</div>;
};

